/**
 * useGestures — reusable touch-gesture hooks.
 *
 * Every hook returns `{ ref, ...handlers }`. Spread the handlers onto the
 * element OR attach `ref` if the hook needs imperative access to the
 * underlying node. All hooks are no-ops on devices without touch (return
 * empty handlers) so desktop behavior is unaffected.
 *
 * Why custom and not a library: we already have a working swipe gesture
 * (drawer swipe-to-close in BAMFull) and want consistent semantics —
 * threshold, vertical-abort, light haptic — across the app without
 * pulling in a 30KB dep.
 */
import { useRef, useCallback, useEffect, useState } from "react";
import { haptic } from "./nativeBridge";

/**
 * useSwipeDown — fires `onDismiss` when the user drags the element down
 * past `threshold` px. Tracks finger via `translateY` for a real
 * rubber-band feel; snaps back if the threshold isn't crossed.
 *
 * Usage on a bottom sheet:
 *   const { onTouchStart, onTouchMove, onTouchEnd, style } = useSwipeDown({
 *     onDismiss: closeSheet,
 *     threshold: 100,
 *   });
 *   <div onTouchStart={...} onTouchMove={...} onTouchEnd={...} style={{...sheet, ...style}}>
 *
 * The hook returns a `style` with the live transform; spread it last so
 * it overrides any static transform on the sheet.
 */
export function useSwipeDown({ onDismiss, threshold = 100, enabled = true } = {}) {
  const stateRef = useRef({ active: false, startY: 0, startX: 0, dy: 0, dx: 0 });
  const elRef = useRef(null);

  const apply = useCallback((dy) => {
    const el = elRef.current; if (!el) return;
    el.style.transform = `translateY(${Math.max(0, dy)}px)`;
    el.style.transition = "none";
  }, []);
  const reset = useCallback(() => {
    const el = elRef.current; if (!el) return;
    el.style.transform = "translateY(0)";
    el.style.transition = "transform .22s cubic-bezier(.2,.8,.2,1)";
  }, []);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    const t = e.touches?.[0]; if (!t) return;
    stateRef.current = { active: true, startY: t.clientY, startX: t.clientX, dy: 0, dx: 0 };
  }, [enabled]);

  const onTouchMove = useCallback((e) => {
    const s = stateRef.current; if (!s.active) return;
    const t = e.touches?.[0]; if (!t) return;
    s.dy = t.clientY - s.startY;
    s.dx = t.clientX - s.startX;
    // Cancel if horizontal — could be a chip-row scroll inside the sheet.
    if (Math.abs(s.dx) > 30 && Math.abs(s.dx) > Math.abs(s.dy) * 1.3) {
      s.active = false; reset(); return;
    }
    // Only follow downward drag — pulling up should not move the sheet.
    if (s.dy > 0) apply(s.dy);
  }, [apply, reset]);

  const onTouchEnd = useCallback(() => {
    const s = stateRef.current; if (!s.active) return;
    s.active = false;
    if (s.dy > threshold) {
      haptic("light");
      if (onDismiss) onDismiss();
      // Don't snap back — caller will unmount.
    } else {
      reset();
    }
  }, [threshold, onDismiss, reset]);

  return {
    ref: elRef,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
  };
}

/**
 * useLongPress — fires `onLongPress` after `delay` ms of continuous press
 * without significant movement. Auto-fires a "selection" haptic to
 * confirm to the user that the long-press was recognized.
 *
 * Movement tolerance: 8px. Past that, treats as a tap-and-drag and
 * cancels the long-press timer.
 */
export function useLongPress({ onLongPress, delay = 500, enabled = true } = {}) {
  const timerRef = useRef(null);
  const stateRef = useRef({ startX: 0, startY: 0, fired: false });

  const cancel = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    const t = e.touches?.[0]; if (!t) return;
    stateRef.current = { startX: t.clientX, startY: t.clientY, fired: false };
    cancel();
    timerRef.current = setTimeout(() => {
      stateRef.current.fired = true;
      haptic("medium");
      if (onLongPress) onLongPress(e);
    }, delay);
  }, [enabled, delay, onLongPress, cancel]);

  const onTouchMove = useCallback((e) => {
    const s = stateRef.current;
    const t = e.touches?.[0]; if (!t) return;
    if (Math.abs(t.clientX - s.startX) > 8 || Math.abs(t.clientY - s.startY) > 8) {
      cancel();
    }
  }, [cancel]);

  const onTouchEnd = useCallback(() => cancel(), [cancel]);

  // didFire helper: callers wrap their onClick so a long-press doesn't
  // also fire the click. Example:
  //   onClick={e => { if (lp.didFire()) return; defaultClick(); }}
  const didFire = useCallback(() => {
    const f = stateRef.current.fired;
    stateRef.current.fired = false;
    return f;
  }, []);

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd, didFire };
}

/**
 * useDoubleTap — fires `onDoubleTap` when two taps land within `gap` ms
 * and within 30px of each other. Returns a `onClick` handler that should
 * be added to the target element.
 *
 * Tap behavior: the first tap is held until the gap window expires; if
 * a second tap arrives in time, `onDoubleTap` fires AND the single-tap
 * handler does NOT fire. If the window expires with no second tap, the
 * single-tap `onClick` callback fires (so single-tap behavior is
 * preserved).
 */
export function useDoubleTap({ onDoubleTap, onSingleTap, gap = 280, enabled = true } = {}) {
  const lastTapRef = useRef({ t: 0, x: 0, y: 0 });
  const pendingSingleRef = useRef(null);

  const onClick = useCallback((e) => {
    if (!enabled) { onSingleTap?.(e); return; }
    const now = Date.now();
    const x = e.clientX ?? 0;
    const y = e.clientY ?? 0;
    const last = lastTapRef.current;
    const dt = now - last.t;
    const dx = Math.abs(x - last.x);
    const dy = Math.abs(y - last.y);
    if (dt < gap && dx < 30 && dy < 30) {
      // Double-tap detected.
      if (pendingSingleRef.current) {
        clearTimeout(pendingSingleRef.current);
        pendingSingleRef.current = null;
      }
      lastTapRef.current = { t: 0, x: 0, y: 0 };
      haptic("light");
      onDoubleTap?.(e);
      return;
    }
    // First tap — defer single handler in case a second comes.
    lastTapRef.current = { t: now, x, y };
    if (onSingleTap) {
      pendingSingleRef.current = setTimeout(() => {
        pendingSingleRef.current = null;
        onSingleTap(e);
      }, gap);
    }
  }, [enabled, gap, onDoubleTap, onSingleTap]);

  return { onClick };
}

/**
 * useSwipeLeft — reveals action buttons when the user swipes a list row
 * to the left, snapping open at `threshold` px. Snaps back on tap-away
 * or another swipe-right.
 *
 * Returns:
 *   { ref, onTouchStart, onTouchMove, onTouchEnd, open, close, isOpen }
 *
 * The hook moves the row via translateX; the action-buttons layer
 * underneath (positioned absolute right:0) reveals as the row slides
 * leftward. Callers render both layers and pass the action width via
 * `actionWidth`.
 */
export function useSwipeLeft({ actionWidth = 80, threshold = 40, enabled = true } = {}) {
  const elRef = useRef(null);
  const stateRef = useRef({ active: false, startX: 0, startY: 0, dx: 0, dy: 0, openAtStart: false });
  const isOpenRef = useRef(false);

  const apply = useCallback((tx) => {
    const el = elRef.current; if (!el) return;
    el.style.transform = `translateX(${tx}px)`;
    el.style.transition = "none";
  }, []);

  const open = useCallback(() => {
    const el = elRef.current; if (!el) return;
    isOpenRef.current = true;
    el.style.transform = `translateX(-${actionWidth}px)`;
    el.style.transition = "transform .22s cubic-bezier(.2,.8,.2,1)";
  }, [actionWidth]);

  const close = useCallback(() => {
    const el = elRef.current; if (!el) return;
    isOpenRef.current = false;
    el.style.transform = "translateX(0)";
    el.style.transition = "transform .22s cubic-bezier(.2,.8,.2,1)";
  }, []);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    const t = e.touches?.[0]; if (!t) return;
    stateRef.current = { active: true, startX: t.clientX, startY: t.clientY, dx: 0, dy: 0, openAtStart: isOpenRef.current };
  }, [enabled]);

  const onTouchMove = useCallback((e) => {
    const s = stateRef.current; if (!s.active) return;
    const t = e.touches?.[0]; if (!t) return;
    s.dx = t.clientX - s.startX;
    s.dy = t.clientY - s.startY;
    // Vertical scroll detection — abort if mostly vertical.
    if (Math.abs(s.dy) > 16 && Math.abs(s.dy) > Math.abs(s.dx) * 1.3) {
      s.active = false; (s.openAtStart ? open : close)(); return;
    }
    const baseX = s.openAtStart ? -actionWidth : 0;
    const next = baseX + s.dx;
    // Clamp: can't go past -actionWidth*1.4 (rubber-band) or 0.
    const clamped = Math.min(0, Math.max(-actionWidth * 1.4, next));
    apply(clamped);
  }, [actionWidth, apply, open, close]);

  const onTouchEnd = useCallback(() => {
    const s = stateRef.current; if (!s.active) return;
    s.active = false;
    if (s.openAtStart) {
      // Was open — close if user swiped right by threshold.
      if (s.dx > threshold) close(); else open();
    } else {
      // Was closed — open if user swiped left by threshold.
      if (s.dx < -threshold) { haptic("light"); open(); } else close();
    }
  }, [threshold, open, close]);

  return {
    ref: elRef,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
    open,
    close,
    get isOpen() { return isOpenRef.current; },
  };
}

/**
 * useScrollToTop — given a ref to a scroll container, returns an
 * imperative handler that smooth-scrolls back to top. Used by the
 * topbar-tap gesture so the user can tap the title to return to the
 * top of any long feed.
 */
export function useScrollToTop(scrollRef) {
  return useCallback(() => {
    const el = scrollRef?.current; if (!el) return;
    try {
      el.scrollTo({ top: 0, behavior: "smooth" });
      haptic("selection");
    } catch {
      el.scrollTop = 0;
    }
  }, [scrollRef]);
}

/**
 * usePullToRefresh — drag down at the top of a scroll container past
 * `threshold` to fire `onRefresh`. Shows a translateY indicator while
 * the user is pulling. Callers attach the returned handlers to the
 * scroll container and render the indicator using `pullDistance`.
 *
 * Returns: { onTouchStart, onTouchMove, onTouchEnd, pullDistance,
 *           refreshing }
 *
 * Refresh handling:
 *   - `onRefresh()` may return a Promise; the spinner stays visible
 *     until it resolves (or 1500ms timeout if it doesn't).
 *   - Only activates when scrollTop === 0; otherwise lets the native
 *     scroll handle the gesture.
 */
export function usePullToRefresh({ onRefresh, threshold = 70, enabled = true, scrollRef } = {}) {
  const stateRef = useRef({ active: false, startY: 0, pulling: 0 });
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const onTouchStart = useCallback((e) => {
    if (!enabled || refreshing) return;
    const el = scrollRef?.current;
    if (el && el.scrollTop > 0) return;
    const t = e.touches?.[0]; if (!t) return;
    stateRef.current = { active: true, startY: t.clientY, pulling: 0 };
  }, [enabled, refreshing, scrollRef]);

  const onTouchMove = useCallback((e) => {
    const s = stateRef.current; if (!s.active) return;
    const t = e.touches?.[0]; if (!t) return;
    const dy = t.clientY - s.startY;
    if (dy <= 0) { s.pulling = 0; setPull(0); return; }
    // Resistance: divide by ~2.2 so the pull feels weighty past 50px.
    const resisted = dy < 50 ? dy : 50 + (dy - 50) / 2.2;
    s.pulling = resisted;
    setPull(resisted);
  }, []);

  const onTouchEnd = useCallback(async () => {
    const s = stateRef.current; if (!s.active) return;
    s.active = false;
    if (s.pulling > threshold && onRefresh) {
      setRefreshing(true);
      haptic("success");
      // Hold the indicator at threshold while refreshing.
      setPull(threshold);
      try {
        const r = onRefresh();
        await Promise.race([
          Promise.resolve(r),
          new Promise(res => setTimeout(res, 1500)),
        ]);
      } catch { /* swallow */ }
      setRefreshing(false);
    }
    setPull(0);
  }, [threshold, onRefresh]);

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd, pullDistance: pull, refreshing };
}
